import React from 'react';

import { ActivityCards } from './activity_cards';
import { ContactCards } from './contact_cards';
import { DealCards } from './deal_cards';
import { EmailCards } from './email_cards';
import { ListingCards } from './listing_cards';
import { ProjectCards } from './project_cards';
import { QuestionCards } from './question_cards';
import { RecommendationSteps } from './recommendation_steps';
import { ResearchPropertyCards } from './research_property_cards';
import { TaskCards } from './task_cards';
import { VoucherCards } from './voucher_cards';

import type { InlineToolCall, ToolCall } from '../types';

type InlineTool = React.ComponentType<{ superseded: boolean; toolCalls: InlineToolCall[] }>;

type InlineToolProps<Name extends InlineToolCall['name']> = {
  superseded: boolean;
  toolCalls: Extract<InlineToolCall, { name: Name }>[];
};

const toolsByName: { [Name in InlineToolCall['name']]: React.ComponentType<InlineToolProps<Name>> } = {
  activity_search: ActivityCards,
  ask_user_questions: QuestionCards,
  contact_load: ContactCards,
  contact_search: ContactCards,
  deal_load: DealCards,
  deal_search: DealCards,
  listing_load: ListingCards,
  listing_search: ListingCards,
  project_load: ProjectCards,
  project_search: ProjectCards,
  recommendations: RecommendationSteps,
  research_property_load: ResearchPropertyCards,
  research_property_search: ResearchPropertyCards,
  send_email: EmailCards,
  task_load: TaskCards,
  task_search: TaskCards,
  voucher_load: VoucherCards,
  voucher_search: VoucherCards,
};

export function isInlineToolCall(toolCall: ToolCall): toolCall is InlineToolCall {
  return toolCall.name in toolsByName;
}

// Inline tools render as part of the transcript instead of interrupting the user, so they never
// reach the standalone queue - the message they arrived with owns them.
export function InlineTools({ superseded, toolCalls }: { superseded: boolean; toolCalls: InlineToolCall[] }) {
  const groups = getToolGroups(toolCalls);
  if (!groups.length) {
    return null;
  }

  return (
    <div className="d-flex flex-column gap-2 mb-2">
      {groups.map(([Tool, groupedCalls], index) => (
        <Tool key={index} superseded={superseded} toolCalls={groupedCalls} />
      ))}
    </div>
  );
}

function getToolGroups(toolCalls: InlineToolCall[]) {
  const groups = new Map<InlineTool, InlineToolCall[]>();

  toolCalls.forEach((toolCall) => {
    const tool = toolsByName[toolCall.name] as InlineTool;
    groups.set(tool, [...(groups.get(tool) || []), toolCall]);
  });

  return [...groups];
}
